import { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import type { DealOption } from '@/types';
import { dealDetails, formatDealAmount } from './dealOption';
import { relativeDays } from './pipelineFormat';

/**
 * Aan welke HubSpot-deal komt de offerte te hangen? Vanaf een dealkaart staat
 * die deal al ingevuld; wie de tool los opent, zoekt hem hier op. Zonder
 * zoekterm staan de laatst gewijzigde deals klaar. Bedrijf, fase, bedrag en
 * laatste wijziging staan erbij, zodat deals met een vergelijkbare naam niet
 * door elkaar raken.
 */
export function DealPicker({
  value,
  onChange,
}: {
  value: DealOption | null;
  onChange: (deal: DealOption | null) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DealOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (value) return;
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      api
        .searchDeals(query.trim())
        .then((deals) => {
          if (cancelled) return;
          setResults(deals);
          setFailed(false);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, value]);

  const now = Date.now();

  if (value) {
    const details = dealDetails(value);
    const amount = formatDealAmount(value);
    const facts = [amount, value.lastModifiedAt && `gewijzigd ${relativeDays(value.lastModifiedAt, now)}`].filter(Boolean);
    return (
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 rounded border px-3 py-2">
        <div>
          <p className="truncate text-sm font-medium">{value.name}</p>
          {details.length > 0 && (
            <p className="truncate text-xs text-muted-foreground" title={details.join(' · ')}>
              {details.join(' · ')}
            </p>
          )}
          {facts.length > 0 && <p className="truncate text-xs text-muted-foreground">{facts.join(' · ')}</p>}
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          Wijzigen
        </button>
      </div>
    );
  }

  return (
    <div className="rounded border">
      <div className="relative border-b">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek een deal…"
          aria-label="Zoek een deal"
          className="h-9 rounded-none border-0 pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>
      <ul className="max-h-72 overflow-y-auto py-1" role="listbox" aria-label="Deals">
        {failed ? (
          <li className="px-3 py-2 text-sm text-muted-foreground">Deals ophalen uit HubSpot is mislukt.</li>
        ) : results.length === 0 && !loading ? (
          <li className="px-3 py-2 text-sm text-muted-foreground">Geen deals gevonden.</li>
        ) : (
          results.map((deal) => {
            const details = dealDetails(deal);
            const amount = formatDealAmount(deal);
            return (
              <li key={deal.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => onChange(deal)}
                  className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 px-3 py-1.5 text-left hover:bg-accent"
                >
                  <span className="truncate text-sm">{deal.name}</span>
                  <span className="text-right text-xs tabular-nums text-muted-foreground">{amount}</span>
                  <span className="truncate text-xs text-muted-foreground" title={details.join(' · ') || undefined}>
                    {details.join(' · ')}
                  </span>
                  <span
                    className="text-right text-xs text-muted-foreground"
                    title={deal.lastModifiedAt ? 'Laatst gewijzigd in HubSpot' : undefined}
                  >
                    {deal.lastModifiedAt && relativeDays(deal.lastModifiedAt, now)}
                  </span>
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}

export default DealPicker;
